import * as React from "react";
import SpreetailHeader from "../layout/SpreetailHeader";
import { useHistory } from "react-router-dom";
import {authConfig} from "../../config/auth-config";
import LoginIcon from '@mui/icons-material/Login';
import { createOrUpdateUser } from '../../../shared/apiService';
import { formatDisplayName } from '../../../shared/nameUtils';

const LoginRoute: React.FC = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = React.useState(false);

  // Use ref to store dialog instance
  const dialogRef = React.useRef<Office.Dialog | null>(null);

  // Handle message from dialog
  const handleDialogMessage = React.useCallback(async (arg: Office.DialogParentMessageReceivedEventArgs) => {
    console.log("handleDialogMessage --------------------------");
    console.log("arg is", arg);
    try {
      if (dialogRef.current) {
        dialogRef.current.close();
      }

      const message = JSON.parse(arg.message);

      if (message.status === 'success') {
        const { accessToken, idToken } = message;
        console.log("Login successful! Tokens received.");
        
        // Store tokens
        if (idToken) {
          try {
            await OfficeRuntime.storage.setItem('token', accessToken);
            await OfficeRuntime.storage.setItem('id_token', idToken);
            console.log("Token stored successfully");
          } catch (storageError) {
            console.error('⚠️ Failed to store token:', storageError);
          }
        }

        // Parse ID token to extract user info
        if (idToken) {
          try {
            // Decode JWT token (base64url decode the payload)
            const base64Url = idToken.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
              atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
            );
            const idTokenClaims = JSON.parse(jsonPayload);
            console.log("idTokenClaims is", idTokenClaims);

            // Extract user info from token claims
            const email = idTokenClaims?.email || '';
            const auth0_user_id = idTokenClaims?.sub || '';
            const rawName =
              idTokenClaims?.name ||
              `${idTokenClaims?.given_name || ''} ${idTokenClaims?.family_name || ''}`.trim() ||
              email ||
              'User';

            const name = formatDisplayName(rawName);

            // Persist basic user info for UI
            try {
              await OfficeRuntime.storage.setItem('user_name', name);
            } catch (storageError) {
              console.error('⚠️ Failed to persist user name to storage:', storageError);
            }

            console.log("email is", email);
            console.log("auth0_user_id is", auth0_user_id);
            console.log("name is", name);
            // Create/update user in database (non-blocking)
            if (email && auth0_user_id) {
              try {
                await createOrUpdateUser({
                  email,
                  auth0_user_id,
                  name
                });
              } catch (userError) {
                console.error('⚠️ Failed to save user data to database:', userError);
              }
            } else {
              console.warn('⚠️ Missing user information from token claims:', {
                email: !!email,
                auth0_user_id: !!auth0_user_id
              });
            }

            // Navigate to home page after successful login
            setIsLoading(false);
            history.push("/");
          } catch (parseError) {
            console.error('⚠️ Failed to parse ID token:', parseError);
            setIsLoading(false);
          }
        }
      } else {
        console.error("Authentication Error:", message.message);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error handling dialog message:", error);
      setIsLoading(false);
    }
  }, [history]);

  const handleLogin = async () => {
    setIsLoading(true);

      const authUrl = `https://${authConfig.domain}/authorize?` +
      `response_type=token id_token&` + // Request tokens explicitly
      `client_id=${authConfig.clientId}&` +
      `redirect_uri=${authConfig.redirectUri}&` +
      `audience=${authConfig.audience}&` + 
      `scope=${authConfig.scope}&` +
      `prompt=login&` +
      `nonce=1234567890`;

    try {
      Office.context.ui.displayDialogAsync(
        authUrl,
        { height: 60, width: 40, displayInIframe: false },
        (asyncResult) => {
          console.log("Async result is", asyncResult);
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            console.error("Dialog failed to open: " + asyncResult.error.message);
            setIsLoading(false);
          } else {
            console.log("Dialog is", asyncResult.value);
            dialogRef.current = asyncResult.value;
            console.log("Dialog ref is", dialogRef.current);
              dialogRef.current.addEventHandler(
                Office.EventType.DialogMessageReceived,
                handleDialogMessage
              );

              dialogRef.current.addEventHandler(
                Office.EventType.DialogEventReceived,
                handleDialogClosed
              );

          }
        }
      );
    } catch (err) {
      console.error("Login error", err);
      setIsLoading(false);
    }
  };

  const handleDialogClosed = () => {
    console.log("Dialog was closed by the user");
    setIsLoading(false);          // stop loader
    dialogRef.current = null;     // cleanup ref
  };


  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .bg-gradient-custom {
            background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
          }
        `}
      </style>
      <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom">
        <div className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none" 
             style={{ background: 'radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)' }}></div>
        <div className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none" 
             style={{ background: 'radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)' }}></div>

        <SpreetailHeader />

        <div className="flex-1 flex items-center justify-center p-5">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-lg shadow-lg p-8 flex flex-col items-center text-center">
              {/* Logo */}
              <div className="mb-6">
                <img 
                  src="/assets/Spreetail_Logo_Text.png" 
                  alt="Spreetail" 
                  style={{ 
                    height: '40px',
                    objectFit: 'contain'
                  }} 
                />
              </div>

              {/* Title */}
              <h1 className="text-2xl font-semibold text-gray-800 mb-2">
                Welcome to Spreetail
              </h1>
              
              {/* Description */}
              <p className="text-sm text-gray-500 mb-8 max-w-sm">
                Create an account or login to continue using our Excel add-in features.
              </p>

              {/* Login Button */}
              <button
                onClick={handleLogin}
                disabled={isLoading}
                className={`w-full text-white border-none rounded-md py-2.5 px-4 text-[13px] font-semibold transition-all duration-200 shadow-md flex items-center justify-center gap-2 ${
                  isLoading
                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                    : 'bg-[#068E75] cursor-pointer hover:bg-[#057A63] hover:-translate-y-px hover:shadow-lg'
                }`}
                style={{ 
                  boxShadow: isLoading ? 'none' : '0 2px 4px rgba(6, 142, 117, 0.3)'
                }}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full" 
                         style={{ animation: 'spin 1s linear infinite' }}></div>
                    <span>Logging in...</span>
                  </>
                ) : (
                  <>
                    <LoginIcon sx={{ fontSize: '16px', color: 'white' }} />
                    <span>Login</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginRoute;
